import { useEffect, useRef } from "react";
import { HomeInfoDomain } from "@/types/HomeInfo";
import { CardOfTheDayStatus } from "./types/CardOfTheDayStatus";
import { AstroPrognosisStatus } from "./types/AstroPrognosisStatus";
import { ZodiacalPrognosisStatus } from "./types/ZodiacalPrognosisStatus";
import { LuckyCardStatus } from "./types/LuckyCardStatus";
import { UserInfoDomain } from "@/types/UserInfo";
import { UserSubscriptionDomain } from "@/types/UserSubscription";
import { userInfoRepository } from "@/services/userInfoRepository";
import { ModalType } from "./types/ModalTypes";

export { ModalType };
import {
  useStatusManagement,
  useModalManagement,
  useNotificationManagement,
  useDataManagement,
  useNavigation,
  useUserActions
} from "./hooks";

export interface HomeScreenViewModel {
  // Состояние загрузки
  isLoading: boolean;
  
  // Модальные окна
  isBottomSheetOpen: boolean;
  isCenterSheetOpen: boolean;
  modalType: ModalType;
  openBottomSheet: (type: ModalType) => void;
  closeBottomSheet: () => void;
  openOnDeleteAccountAlert: () => void;
  closeCenterSheet: () => void;
  
  // Уведомления
  isPushNotificationEnabled: boolean;
  permission: NotificationPermission;
  isVisiblePermissionAlert: boolean;
  isSupported: boolean;
  handleEnableNotifications: () => void;
  closePermissionAlert: () => void;
  requestPermission: () => Promise<NotificationPermission | null>;
  aboutText: string;
  // Данные пользователя
  userInfo: UserInfoDomain | null;
  subscription: UserSubscriptionDomain | null;

  
  // Статусы карт и прогнозов
  cardOfTheDayStatus: CardOfTheDayStatus | null;
  astroPrognosisStatus: AstroPrognosisStatus | null;
  zodiacalPrognosisStatus: ZodiacalPrognosisStatus | null;
  luckyCardStatus: LuckyCardStatus | null;
  
  // Действия
  logout: () => void;
  deleteAccount: (text: string) => void;
  
  // Навигация
  navigateToChat: () => void;
  navigateToCardOfTheDay: () => void;
  navigateToAstroPrognosis: () => void;
  navigateToZodiacPrognosis: () => void;
  navigateToLuckyCard: () => void;
  navigateToAstrologChanel: () => void;
  navigateToDeveloperChanel: () => void;
  navigateToSubscription: () => void;
}

export const useHomeScreenViewModel = (): HomeScreenViewModel => {
  const modalManagement = useModalManagement();
  const notificationManagement = useNotificationManagement();
  const dataManagement = useDataManagement();
  const statusManagement = useStatusManagement();
  const navigation = useNavigation();
  const userActions = useUserActions();
  const homeInfoRef = useRef<HomeInfoDomain | null>(null);
  const isSubscribedRef = useRef(false);

  const refreshTimerStatuses = () => {
    const homeInfo = homeInfoRef.current;
    if (!homeInfo) return;

    statusManagement.updateCardOfTheDayStatus(homeInfo);
    statusManagement.updateAstroPrognosisStatus(homeInfo);
    statusManagement.updateZodiacalPrognosisStatus(isSubscribedRef.current, homeInfo);
    statusManagement.updateLuckyCardStatus(homeInfo, isSubscribedRef.current);
  };

  useEffect(() => {
    const initializeScreen = async () => {
      dataManagement.setIsLoading(true);
      
      try {
        const { response, isSubscribed, config } = await dataManagement.updateHomeInfo();
        
        if (response) {
          homeInfoRef.current = response;
          isSubscribedRef.current = isSubscribed;
          statusManagement.updateCardOfTheDayStatus(response);
          statusManagement.updateAstroPrognosisStatus(response);
          statusManagement.updateZodiacalPrognosisStatus(isSubscribed, response);
          statusManagement.updateLuckyCardStatus(response, isSubscribed);
          statusManagement.updateAboutText(config?.aboutApp ?? "");
        }
        
        // Инициализация уведомлений (безопасная для iOS)
        try {
          const isEnabled = userInfoRepository.isEnabledPushNotifications();
          notificationManagement.setIsPushNotificationEnabled?.(isEnabled);
        } catch (error) {
          console.warn('Failed to initialize notifications:', error);
          // Продолжаем работу без уведомлений
        }
      } catch (error) {
        console.error("Error initializing screen:", error);
      } finally {
        dataManagement.setIsLoading(false);
      }
    };

    initializeScreen();
  }, []);

  useEffect(() => {
    const interval = setInterval(refreshTimerStatuses, 60_000);
    return () => clearInterval(interval);
  }, []);

  return {
    // Состояние загрузки
    isLoading: dataManagement.isLoading,
    
    // Модальные окна
    ...modalManagement,
    
    // Уведомления
    ...notificationManagement,
    
    // Данные пользователя
    userInfo: dataManagement.userInfo,
    subscription: dataManagement.subscription,
    aboutText: statusManagement.aboutText,
    // Статусы карт и прогнозов
    cardOfTheDayStatus: statusManagement.cardOfTheDayStatus,
    astroPrognosisStatus: statusManagement.astroPrognosisStatus,
    zodiacalPrognosisStatus: statusManagement.zodiacalPrognosisStatus,
    luckyCardStatus: statusManagement.luckyCardStatus,
    
    // Действия
    ...userActions,
    
    // Навигация
    ...navigation,
  };
};
